'use client';

import type { GtmEvent } from './types';
import { isMobile } from '@/utils/devices';
import { getConfig } from '@/utils/config/config';
import { isAndroidWrapper, isIosWrapper } from '@/logic/platform';

const GTM_READY_EVENT = 'gtm.js';
const FLUSH_RETRY_MS = 100;
const MAX_WAIT_MS = 10000;
let queue: GtmEvent[] = [];
let isReady = false;

const logEvent = (trackingEvent: GtmEvent) => {
    const { event, ...params } = trackingEvent;
    if (window.FirebaseAnalyticsWebInterface) {
        // Call Android interface
        window.FirebaseAnalyticsWebInterface.logEvent(event, JSON.stringify(params));
    } else if (window?.webkit?.messageHandlers?.firebase) {
        // Call iOS interface
        const message = {
            command: 'logEvent',
            name: event,
            parameters: params
        };
        window.webkit.messageHandlers.firebase.postMessage(message);
    } else {
        // No Android or iOS interface found
        console.log('No native APIs found.');
    }
};

const push = (params: GtmEvent) => {
    window.dataLayer?.push(params);

    if (isMobile() && (isAndroidWrapper() || isIosWrapper())) {
        logEvent(params);
    }
};

const isGtmReady = () =>
    (window.dataLayer as GtmEvent[] | undefined)?.some((entry) => entry?.event === GTM_READY_EVENT) ?? false;

const flushWhenReady = () => {
    const flush = () => {
        isReady = true;
        queue.forEach(push);
        queue = [];
    };

    if (isGtmReady()) {
        flush();
        return;
    }

    const startedAt = Date.now();
    const interval = setInterval(() => {
        if (isGtmReady() || Date.now() - startedAt > MAX_WAIT_MS) {
            clearInterval(interval);
            flush();
        }
    }, FLUSH_RETRY_MS);
};

const gtmPush = (params: GtmEvent) => {
    if (!getConfig().services.ga.enabled) {
        return;
    }

    window.dataLayer = window.dataLayer || [];

    if (isReady) {
        push(params);
        return;
    }

    queue.push(params);
    if (queue.length === 1) {
        flushWhenReady();
    }
};

export { gtmPush };
